import type { IconName } from 'icons/types/IconName.types'
import {
	type ForwardedRef,
	type MouseEvent,
	type KeyboardEvent as ReactKeyboardEvent,
	forwardRef,
	useCallback,
	useEffect,
	useRef,
} from 'react'
import { cx } from 'utils/cx'

import type { OptionsType } from '..'
import { type IconOptionItems, OptionItem } from '../OptionItem/OptionItem'

import styles from './Dropdown.module.scss'

export interface DropdownProps {
	readonly closeDropdown: () => void
	readonly options: OptionsType[]
	readonly onChange: (
		event: MouseEvent<HTMLDivElement> | ReactKeyboardEvent<HTMLDivElement>
	) => void
	readonly icon?: IconName
	readonly iconShowCondition?: (option: OptionsType) => boolean
	readonly className?: string
	readonly value?: string | number
	readonly dataTestId?: string
	readonly id?: string
	readonly open: boolean
	readonly placeholder?: string
	readonly autoWidth: boolean
}

enum DropdownIndex {
	First = '0',
	Second = '1',
}

export const Dropdown = forwardRef<HTMLDivElement, DropdownProps>(
	(props: DropdownProps, ref: ForwardedRef<HTMLDivElement | null>) => {
		const {
			options,
			onChange,
			icon,
			iconShowCondition,
			className = '',
			value,
			id,
			dataTestId,
			open,
			closeDropdown,
			placeholder,
			autoWidth,
		} = props

		const refDropdown = useRef<HTMLDivElement | null>(null)

		useEffect(() => {
			if (!ref) {
				return
			}

			if (typeof ref === 'function') {
				ref(refDropdown.current)
			} else {
				ref.current = refDropdown.current
			}
		}, [ref])

		const handleHomeAndEndActions = useCallback(
			(
				items: NodeListOf<HTMLElement> | undefined,
				currentItemIndex: number,
				newRefDropdown: string
			) => {
				if (!items) {
					return
				}
				const currentItem = items[currentItemIndex]
				currentItem.focus()
				if (refDropdown.current) {
					refDropdown.current.dataset.index = newRefDropdown
				}
			},
			[]
		)

		const actions = useCallback(
			(event: KeyboardEvent, items: NodeListOf<HTMLElement> | undefined) => ({
				ArrowDown: () => {
					const currentItemFocus = Number.parseInt(
						refDropdown.current?.dataset.index ?? DropdownIndex.First,
						10
					)
					const currentItemFocusIndex = currentItemFocus - 1
					if (!items || currentItemFocusIndex > items.length - 1) {
						return
					}
					const currentItem = items[currentItemFocusIndex + 1]
					currentItem.focus()
					if (refDropdown.current) {
						refDropdown.current.dataset.index = (
							currentItemFocus + 1
						).toString()
					}
				},
				ArrowUp: () => {
					const currentItemFocus = Number.parseInt(
						refDropdown.current?.dataset.index ?? DropdownIndex.First,
						10
					)
					const currentItemFocusIndex = currentItemFocus - 1
					if (!items || currentItemFocusIndex <= 0) {
						return
					}
					const currentItem = items[currentItemFocusIndex - 1]
					currentItem.focus()
					if (refDropdown.current) {
						refDropdown.current.dataset.index = (
							currentItemFocus - 1
						).toString()
					}
				},
				Escape: () => {
					closeDropdown()
					const sibling = refDropdown.current
						?.previousElementSibling as HTMLElement
					sibling.focus()
				},
				Enter: () => {
					const target = event.target as HTMLElement
					target?.setAttribute('aria-selected', 'true')
					const sibling = refDropdown.current
						?.previousElementSibling as HTMLElement
					sibling.focus()
				},
				Home: () => {
					handleHomeAndEndActions(items, 0, DropdownIndex.Second)
				},
				End: () => {
					if (items) {
						handleHomeAndEndActions(
							items,
							items.length - 1,
							items.length.toString()
						)
					}
				},
				Tab: () => {
					closeDropdown()
					const sibling = refDropdown.current
						?.previousElementSibling as HTMLElement
					sibling.focus()
				},
			}),
			[closeDropdown]
		)

		const handleKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
			event.preventDefault()
			const items = refDropdown.current?.querySelectorAll(
				'[role="option"]'
			) as NodeListOf<HTMLElement>
			const action: { [key: string]: () => void } = actions(
				event.nativeEvent,
				items
			)
			if (action[event.key]) {
				action[event.key]()
			}
		}

		const dropDownClassName = cx(
			styles.listOptions,
			!open && styles.closed,
			!!autoWidth && styles.autoWidth,
			className
		)

		return (
			<div
				id={`${id}.listbox`}
				data-testid={`${dataTestId}.listbox`}
				ref={refDropdown}
				onKeyDown={handleKeyDown}
				// biome-ignore lint/a11y/useSemanticElements: `div` is used for custom component
				role='listbox'
				tabIndex={-1}
				className={dropDownClassName}
				aria-labelledby={`${id}.label`}
			>
				{placeholder && (
					<OptionItem
						text={placeholder}
						value=''
						onChange={onChange}
						toggle={closeDropdown}
					/>
				)}
				{options?.map((option: OptionsType) => (
					<OptionItem
						key={`${option.value}${option.text ? String(option.text) : ''}`}
						onChange={onChange}
						toggle={closeDropdown}
						icon={icon as IconOptionItems}
						text={option.text}
						value={option.value}
						showIcon={iconShowCondition?.(option)}
						selected={value}
						disabled={option.disabled}
					/>
				))}
			</div>
		)
	}
)

Dropdown.displayName = 'Dropdown'
